import { BuildingOffice2Icon, CheckCircleIcon, XCircleIcon, DocumentCheckIcon, DocumentChartBarIcon, DocumentIcon, IdentificationIcon, ChatBubbleLeftRightIcon, KeyIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import Modal from "components/common/Modal";
import { customerDisplayName } from "../../utils/customerName";
import { Customer, CustomerFormData } from "types/customer";
import { formatDocumentDate, formatAddress } from "utils/formatters";
import { useLanguage } from 'contexts/LanguageContext';
import { SimpleOrganization } from 'pages/Customers';
import { useCustomFields } from '../../hooks/useCustomFields';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCustomers } from '../../hooks/useCustomers';
import CustomerRelationshipLogTab from './CustomerRelationshipLogTab';
import { userService } from '../../services/userService';
import CustomerUsersManager from './CustomerUsersManager';
import ContractList from '../contracts/ContractList';
import {
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  UserIcon,
  ChevronRightIcon,
  UsersIcon
} from '@heroicons/react/24/outline';


interface CustomerViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  organizations: SimpleOrganization[];
  onEdit: (customer: Customer) => void;
}

const CustomerViewModal: React.FC<CustomerViewModalProps> = ({
  isOpen,
  onClose,
  customer,
  organizations,
  onEdit
}) => {
  const { t } = useLanguage();
  const { refreshCustomer, customers } = useCustomers();
  const { customFields } = useCustomFields('customer');
  const [refreshKey, setRefreshKey] = useState(0);
  const [localCustomer, setLocalCustomer] = useState<Customer | null>(customer);
  type SectionId = 'details' | 'logs' | 'history' | 'access' | 'contracts';
  const [activeSection, setActiveSection] = useState<SectionId>('details');
  const sections: { id: SectionId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'details', label: t('customers.tabs.details'), icon: IdentificationIcon },
    { id: 'logs', label: t('customers.tabs.relationshipLog'), icon: ChatBubbleLeftRightIcon },
    { id: 'history', label: t('customers.tabs.history'), icon: ClockIcon },
    { id: 'access', label: t('customers.tabs.access'), icon: KeyIcon },
    { id: 'contracts', label: 'Szerződések', icon: DocumentTextIcon },
  ];

  // Keyboard navigation for tabs
  const handleKeyDown = useCallback((event: React.KeyboardEvent, section: 'details' | 'logs' | 'history' | 'access' | 'contracts') => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setActiveSection(section);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const sections: ('details' | 'logs' | 'history' | 'access' | 'contracts')[] = ['details', 'logs', 'history', 'access', 'contracts'];
      const currentIndex = sections.indexOf(activeSection);
      const nextIndex = event.key === 'ArrowRight' 
        ? (currentIndex + 1) % sections.length 
        : (currentIndex - 1 + sections.length) % sections.length;
      setActiveSection(sections[nextIndex]);
    }
  }, [activeSection]);
  
  // Use the most up-to-date customer data from the customers array
  useEffect(() => {
    if (customer) {
      // First set from the prop to ensure we have data immediately
      setLocalCustomer(customer);
      
      // Then check if we have a more recent version in the customers array
      const updatedCustomer = customers.find(c => c.id === customer.id);
      if (updatedCustomer) {
        setLocalCustomer(updatedCustomer);
      }
    } else {
      setLocalCustomer(null);
    }
  }, [customer, customers]);
  
  // Calculate customer name (must be before early return)
  const customerName = useMemo(() => {
    if (!localCustomer) return '';
    return customerDisplayName(localCustomer);
  }, [localCustomer?.type, localCustomer?.companyName, localCustomer?.firstName, localCustomer?.lastName]);

  if (!localCustomer) {
    return null;
  }

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="animate-pulse space-y-4 p-6">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
      <div className="space-y-2">
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
      </div>
    </div>
  );

  const formattedCustomer = localCustomer as unknown as CustomerFormData;
  
  const handleLogsUpdated = async () => {
    // Force a refresh by updating the refresh key and refreshing customer data
    setRefreshKey(prev => prev + 1);
    
    // Refresh the customer data from the database to get the latest logs
    if (localCustomer && localCustomer.id) {
      try {
        await refreshCustomer(localCustomer.id);
      } catch (error) {
        console.error('Failed to refresh customer data:', error);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={customerName}
      size="7xl"
      noPadding
    >
      <div className="flex flex-col lg:flex-row min-h-[calc(100dvh-3.25rem)] lg:min-h-0 lg:h-full">
        {/* Fülek – mobilon görgethető pill-sor */}
        <nav className="lg:hidden sticky top-0 z-10 bg-[var(--crm-surface)] border-b border-[var(--crm-border)]" role="tablist" aria-label="Ügyfél információ szekciók">
          <div className="flex gap-1.5 overflow-x-auto px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {sections.map(sec => (
              <button
                key={sec.id}
                role="tab"
                aria-selected={activeSection === sec.id}
                aria-controls={`${sec.id}-panel`}
                onClick={() => setActiveSection(sec.id)}
                onKeyDown={(e) => handleKeyDown(e, sec.id)}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  activeSection === sec.id
                    ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                <sec.icon className="w-4 h-4" />
                {sec.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Oldalsáv – asztali */}
        <div className="hidden lg:flex w-64 flex-shrink-0 bg-[var(--crm-bg)] border-r border-[var(--crm-border)] flex-col">
          <div className="p-3 flex-1">
            <nav className="space-y-0.5" aria-label="Szekciók">
              {sections.map(sec => (
                <button
                  key={sec.id}
                  onClick={() => setActiveSection(sec.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left rounded-lg text-sm transition-colors ${
                    activeSection === sec.id
                      ? 'bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <sec.icon className={`w-4 h-4 flex-shrink-0 ${activeSection === sec.id ? '' : 'text-gray-400'}`} />
                  <span className="truncate">{sec.label}</span>
                </button>
              ))}
            </nav>
          </div>
          <div className="p-3 border-t border-[var(--crm-border)] space-y-2">
            <button onClick={() => { onClose(); onEdit(localCustomer); }} className="w-full crm-btn crm-btn-primary">
              <PencilSquareIcon className="w-4 h-4" />
              {t('common.edit')}
            </button>
            <button onClick={onClose} className="w-full crm-btn crm-btn-secondary">
              {t('common.close')}
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0 bg-[var(--crm-surface)] lg:rounded-r-lg lg:overflow-hidden">
          <div className="lg:h-full lg:overflow-y-auto px-4 py-4 lg:p-6">
            {/* Details Content */}
            {activeSection === 'details' && (
              <div id="details-panel" role="tabpanel" aria-labelledby="details-tab">
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                    {t('customers.tabs.details')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Az ügyfél alapvető információi és kapcsolati adatai
                  </p>
                </div>
              {/* Customer details in unified table format */}
              <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" colSpan={2}>
                        {t('customers.form.type.title')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr>
                      <td className="px-3 lg:px-6 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 w-1/3 align-top">
                        {t('customers.form.type.title')}
                      </td>
                      <td className="px-3 lg:px-6 py-3 text-sm text-gray-900 dark:text-gray-200">
                        {localCustomer.type === 'organization'
                          ? t('customers.form.type.organization')
                          : t('customers.form.type.individual')}
                      </td>
                    </tr>
                    {localCustomer.type === 'organization' ? (
                      <tr>
                        <td className="px-3 lg:px-6 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 align-top">
                          {t('customers.form.contractDetails.companyName')}
                        </td>
                        <td className="px-3 lg:px-6 py-3 text-sm text-gray-900 dark:text-gray-200">
                          {localCustomer.companyName || '-'}
                        </td>
                      </tr>
                    ) : (
                      <>
                        <tr>
                          <td className="px-3 lg:px-6 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 align-top">
                            {t('customers.fields.firstName')}
                          </td>
                          <td className="px-3 lg:px-6 py-3 text-sm text-gray-900 dark:text-gray-200">
                            {localCustomer.firstName || '-'}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-3 lg:px-6 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 align-top">
                            {t('customers.fields.lastName')}
                          </td>
                          <td className="px-3 lg:px-6 py-3 text-sm text-gray-900 dark:text-gray-200">
                            {localCustomer.lastName || '-'}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td className="px-3 lg:px-6 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 align-top">
                        {t('customers.fields.status')}
                      </td>
                      <td className="px-3 lg:px-6 py-3 text-sm text-gray-900 dark:text-gray-200">
                        {localCustomer.status === 'active' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 dark:text-green-400 mr-1" />
                            {t('customers.status.active')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                            {t('customers.status.inactive')}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Contract details table */}
              <div className="overflow-hidden border border-gray-200 dark:border-gray-700 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" colSpan={2}>
                        {t('customers.form.contractDetails.title')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr>
                      <td className="px-3 lg:px-6 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 align-top">
                        {t('customers.form.contractDetails.taxNumber')}
                      </td>
                      <td className="px-3 lg:px-6 py-3 text-sm text-gray-900 dark:text-gray-200">
                        {formattedCustomer.taxNumber || '-'}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 lg:px-6 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 align-top">
                        {t('customers.form.contractDetails.registrationNumber')}
                      </td>
                      <td className="px-3 lg:px-6 py-3 text-sm text-gray-900 dark:text-gray-200">
                        {formattedCustomer.registrationNumber || '-'}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 lg:px-6 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 align-top">
                        {t('customers.form.contractDetails.bankAccount')}
                      </td>
                      <td className="px-3 lg:px-6 py-3 text-sm text-gray-900 dark:text-gray-200">
                        {formattedCustomer.bankAccount || '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Contact Persons table */}
              <div className="overflow-hidden border border-gray-200 dark:border-gray-700 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Kapcsolattartók
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {localCustomer.contactPersons && localCustomer.contactPersons.length > 0 ? (
                      localCustomer.contactPersons.map((cp, index) => (
                        <tr key={cp.id || index}>
                          <td className="px-3 lg:px-6 py-3">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                                  <UserIcon className="h-5 w-5 text-white" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {cp.name}
                                  </p>
                                  {cp.isPrimary && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                      Elsődleges
                                    </span>
                                  )}
                                  {cp.hasUserAccount && (
                                    <span className="text-xs text-green-600 dark:text-green-400">
                                      ✓ Regisztrált
                                    </span>
                                  )}
                                </div>
                                {cp.position && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {cp.position}
                                  </p>
                                )}
                                <div className="mt-1 flex flex-col space-y-1">
                                  {cp.email && (
                                    <a href={`mailto:${cp.email}`} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">
                                      {cp.email}
                                    </a>
                                  )}
                                  {cp.phone && (
                                    <a href={`tel:${cp.phone}`} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">
                                      {cp.phone}
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="px-3 lg:px-6 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                          Nincs kapcsolattartó hozzáadva
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Notes and organizations */}
              <div className="overflow-hidden border border-gray-200 dark:border-gray-700 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" colSpan={2}>
                        {t('customers.form.organizations.title')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr>
                      <td className="px-6 py-4" colSpan={2}>
                        <div className="flex flex-wrap gap-2">
                          {localCustomer.organizations && localCustomer.organizations.length > 0 ? (
                            localCustomer.organizations.map(orgId => {
                              const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
                              return org ? (
                                <div
                                  key={org.id}
                                  className="inline-flex items-center px-3 py-1 rounded text-sm font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border border-primary-100 dark:border-primary-800"
                                >
                                  <BuildingOffice2Icon className="w-4 h-4 mr-2" />
                                  {org.name}
                                </div>
                              ) : null;
                            })
                          ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{t('customers.viewModal.noOrganizations')}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Notes */}
              <div className="overflow-hidden border border-gray-200 dark:border-gray-700 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" colSpan={2}>
                        {t('customers.form.organizations.notes')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr>
                      <td className="px-6 py-4" colSpan={2}>
                        <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{localCustomer.notes || '-'}</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Documents table */}
              <div className="overflow-hidden border border-gray-200 dark:border-gray-700 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" colSpan={2}>
                        {t('customers.form.documents.title')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {localCustomer.documents && localCustomer.documents.length > 0 ? (
                      localCustomer.documents.map((document) => (
                        <tr key={document.id}>
                          <td className="px-3 lg:px-6 py-4">
                            <div className="flex items-center">
                              {document.type === 'contract' && <DocumentCheckIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2" />}
                              {document.type === 'quotation' && <DocumentChartBarIcon className="w-5 h-5 text-orange-500 dark:text-orange-400 mr-2" />}
                              {(!document.type || document.type === 'other') && <DocumentIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-2" />}
                              <div>
                                <span className="text-sm text-gray-800 dark:text-gray-200 truncate block max-w-xs">
                                  {document.originalName || document.name}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{formatDocumentDate(document.uploadedAt)}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 lg:px-6 py-4 text-right text-sm font-medium">
                            <a
                              href={document.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 bg-primary-50 dark:bg-primary-900/20 px-3 py-1 rounded-md"
                            >
                              {t('customers.form.documents.view')}
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="px-3 lg:px-6 py-4 text-sm text-gray-500 dark:text-gray-400" colSpan={2}>
                          {t('customers.documentModal.noDocuments')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Custom Fields table */}
              {customFields.length > 0 && localCustomer.customFields && Object.keys(localCustomer.customFields).some(key => {
                const value = localCustomer.customFields?.[key];
                return value !== undefined && value !== null && value !== '';
              }) && (
                <div className="overflow-hidden border border-gray-200 dark:border-gray-700 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
                      <tr>
                        <th scope="col" className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-purple-700 dark:text-purple-300 uppercase tracking-wider" colSpan={2}>
                          Egyedi mezők
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {customFields.map(field => {
                        const value = localCustomer.customFields?.[field.key];
                        if (value === undefined || value === null || value === '') return null;
                        
                        return (
                          <tr key={field.id}>
                            <td className="px-3 lg:px-6 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 w-1/3 align-top">
                              {field.name}
                            </td>
                            <td className="px-3 lg:px-6 py-3 text-sm text-gray-900 dark:text-gray-200">
                              {Array.isArray(value) ? (
                                <div className="flex flex-wrap gap-1">
                                  {value.map((val: string, idx: number) => (
                                    <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                                      {val}
                                    </span>
                                  ))}
                                </div>
                              ) : field.type === 'checkbox' ? (
                                <span>{value ? '✓ Igen' : '✗ Nem'}</span>
                              ) : field.type === 'date' ? (
                                <span>{new Date(value).toLocaleDateString('hu-HU')}</span>
                              ) : (
                                <span>{String(value)}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
            )}

            {/* Relationship Log Content */}
            {activeSection === 'logs' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                    {t('customers.tabs.relationshipLog')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Ügyfélkapcsolati tevékenységek és feladatok nyilvántartása
                  </p>
                </div>
                <CustomerRelationshipLogTab 
                  logs={localCustomer.relationshipLogs} 
                  customerId={localCustomer.id}
                  customerName={customerName}
                  customer={localCustomer}
                  onLogsUpdated={handleLogsUpdated}
                  key={`log-tab-${refreshKey}`}
                />
              </div>
            )}

            {/* History Content */}
            {activeSection === 'history' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                    {t('customers.tabs.history')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Az ügyfél adatainak részletes módosítási előzményei
                  </p>
                </div>
                <CustomerHistoryTab 
                  customerId={localCustomer.id} 
                  customerName={customerName}
                  organizationsList={organizations}
                  customFields={customFields}
                />
              </div>
            )}

            {/* Access Content */}
            {activeSection === 'access' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                    <UsersIcon className="h-6 w-6 mr-2" />
                    {t('customers.tabs.access')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Ügyfélportál hozzáférések kezelése ehhez a szervezethez
                  </p>
                </div>
                <CustomerUsersManager 
                  organizationId={localCustomer.id} 
                  organizationName={customerName}
                />
              </div>
            )}

            {/* Contracts Content */}
            {activeSection === 'contracts' && localCustomer && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-orange-600 dark:text-orange-400" />
                    Szerződések
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Ügyfél szerződései és szolgáltatásai
                  </p>
                </div>
                <ContractList 
                  customerId={localCustomer.id} 
                  customerName={localCustomer.companyName || `${localCustomer.firstName} ${localCustomer.lastName}`.trim()}
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Mobil akciósáv – alul rögzítve */}
        <div className="lg:hidden mt-auto sticky bottom-0 z-10 border-t border-[var(--crm-border)] p-3 bg-[var(--crm-surface)] [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex gap-2">
            <button onClick={onClose} className="crm-btn crm-btn-secondary flex-1 h-11">
              {t('common.close')}
            </button>
            <button onClick={() => { onClose(); onEdit(localCustomer); }} className="crm-btn crm-btn-primary flex-[2] h-11">
              <PencilSquareIcon className="w-4 h-4" />
              {t('common.edit')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Component to display customer history
const CustomerHistoryTab: React.FC<{ 
  customerId: string;
  customerName: string;
  organizationsList: SimpleOrganization[];
  customFields: any[];
}> = ({ customerId, customerName, organizationsList, customFields }) => {
  const { loadCustomerHistory, customerHistory, historyLoading } = useCustomers();
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  
  // Load user names for displaying in history
  const loadUserNames = useCallback(async (history: any[]) => {
    const uniqueUserIds = Array.from(new Set(history.map(h => h.userId).filter(Boolean)));
    const newUserNames: Record<string, string> = {};

    for (const userId of uniqueUserIds) {
      if (!userNames[userId] && !userId.includes('@')) { // Skip if already loaded or if it's an email
        try {
          const userData = await userService.getUser(userId);
          if (userData) {
            newUserNames[userId] = userData.displayName || userData.email || 'Ismeretlen felhasználó';
          } else {
            newUserNames[userId] = `Felhasználó ${userId.slice(-6)}`;
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          newUserNames[userId] = `Felhasználó ${userId.slice(-6)}`;
        }
      }
    }

    if (Object.keys(newUserNames).length > 0) {
      setUserNames(prev => ({ ...prev, ...newUserNames }));
    }
  }, [userNames]);
  
  // Use useCallback for the fetch function to prevent infinite loops
  const fetchHistory = useCallback(async () => {
    if (!customerId || hasAttemptedLoad) return;
    
    try {
      setLocalLoading(true);
      setError(null);
      // Fetching history for customer: ${customerId}
      await loadCustomerHistory(customerId);
    } catch (err) {
      console.error("Error loading history:", err);
      setError("Failed to load history data");
    } finally {
      setLocalLoading(false);
      setHasAttemptedLoad(true);
    }
  }, [customerId, hasAttemptedLoad, loadCustomerHistory]);
  
  // Only fetch history once when the component mounts
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Load user names when history data changes
  useEffect(() => {
    const history = customerHistory[customerId] || [];
    if (history.length > 0) {
      loadUserNames(history);
    }
  }, [customerHistory, customerId, loadUserNames]);
  
  const history = customerHistory[customerId] || [];
  // Customer history data: ${customerId}, records: ${history.length}
  
  // Use local loading state that we control instead of the global one
  if (localLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <ArrowPathIcon className="w-6 h-6 text-gray-400 dark:text-gray-500 animate-spin" />
        <span className="ml-3 text-gray-500 dark:text-gray-400">{t('common.loading')}</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-16 px-4">
        <InformationCircleIcon className="w-12 h-12 text-red-300 dark:text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-600 dark:text-red-400 mb-2">Error loading history</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {error}
        </p>
      </div>
    );
  }
  
  if (history.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <InformationCircleIcon className="w-12 h-12 text-gray-300 dark:text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">{t('customers.history.noHistory')}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {t('customers.history.noHistoryDetail')}
        </p>
      </div>
    );
  }
  
  return (
    <div className="max-h-[60vh] overflow-y-auto px-4">
      <div className="py-4">
        <h3 className="text-base font-medium text-gray-900 dark:text-gray-200 mb-4">
          {t('customers.history.title', { name: customerName })}
        </h3>
        
        <div className="space-y-6">
          {/* Show virtualization hint for large lists */}
          {history.length > 50 && (
            <div className="text-sm text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              📊 {history.length} módosítás található. Nagy mennyiségű adat miatt a görgetés optimalizált.
            </div>
          )}
          {history.map((entry) => (
            <div key={entry.id} className="border-l-2 border-gray-200 dark:border-gray-600 pl-4 relative">
              {/* Timestamp */}
              <div className={`absolute -left-2 top-0 w-4 h-4 rounded-full border-2 ${
                entry.changeType === 'create' 
                  ? 'bg-green-100 dark:bg-green-900 border-green-400 dark:border-green-600' 
                  : entry.changeType === 'update' 
                  ? 'bg-blue-100 dark:bg-blue-900 border-blue-400 dark:border-blue-600'
                  : 'bg-red-100 dark:bg-red-900 border-red-400 dark:border-red-600'
              }`}>
                <div className={`w-2 h-2 rounded-full absolute top-0.5 left-0.5 ${
                  entry.changeType === 'create' 
                    ? 'bg-green-500' 
                    : entry.changeType === 'update' 
                    ? 'bg-blue-500'
                    : 'bg-red-500'
                }`}></div>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center">
                <ClockIcon className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                <span className="font-medium">
                {new Date(typeof entry.timestamp === 'object' && 'toDate' in entry.timestamp ? 
                  entry.timestamp.toDate() : entry.timestamp).toLocaleString('hu-HU', {
                    year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  })}
                </span>
              </div>
              
              {/* Change type */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    entry.changeType === 'create' 
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                      : entry.changeType === 'update' 
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                      : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                  }`}>
                    <span className="mr-1">
                      {entry.changeType === 'create' && '✨'}
                      {entry.changeType === 'update' && '📝'}
                      {entry.changeType === 'delete' && '🗑️'}
                    </span>
                {entry.changeType === 'create' && t('customers.history.created')}
                {entry.changeType === 'update' && t('customers.history.updated')}
                {entry.changeType === 'delete' && t('customers.history.deleted')}
                  </div>
                </div>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  <UserIcon className="w-3 h-3 mr-1" />
                  <span>{userNames[entry.userId] || entry.userId}</span>
                </div>
              </div>
              
              {/* Changed fields */}
              <div className="space-y-2 mt-2">
                {Object.entries(entry.changedFields).map(([field, change]) => {
                  // Check if this is a complex field like POD data
                  const isPodRelated = field === 'pod' || field === 'additionalPods';
                  const isLargeObject = 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                      JSON.stringify(change.oldValue).length > 200) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                      JSON.stringify(change.newValue).length > 200);
                  
                  // Check if this is a contact person field
                  const isContactData = field === 'contactPerson' || field === 'financialContact' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.name !== undefined || change.oldValue.email !== undefined || 
                      change.oldValue.phone !== undefined || change.oldValue.fax !== undefined || 
                      change.oldValue.position !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.name !== undefined || change.newValue.email !== undefined || 
                      change.newValue.phone !== undefined || change.newValue.fax !== undefined || 
                      change.newValue.position !== undefined));
                  
                  // Check if this is consumption data
                  const isConsumptionData = field === 'consumptionData' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.consumption !== undefined || change.oldValue.date !== undefined || 
                      change.oldValue.dueDate !== undefined || change.oldValue.power !== undefined || 
                      change.oldValue.profileNumber !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.consumption !== undefined || change.newValue.date !== undefined || 
                      change.newValue.dueDate !== undefined || change.newValue.power !== undefined || 
                      change.newValue.profileNumber !== undefined));
                  
                  // Check if this is organizations field
                  const isOrganizationsData = field === 'organizations';
                  
                  // Check if this is contract period field
                  const isContractPeriodData = field === 'contractPeriod' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.startDate !== undefined || change.oldValue.endDate !== undefined || 
                      change.oldValue.price !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.startDate !== undefined || change.newValue.endDate !== undefined || 
                      change.newValue.price !== undefined));
                  
                  // Check if this is offer price field
                  const isOfferPriceData = field === 'offerPrice' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.price !== undefined || change.oldValue.currency !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.price !== undefined || change.newValue.currency !== undefined));
                  
                  // Check if this is KAT data
                  const isKatData = field === 'kat' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.kat !== undefined || change.oldValue.text !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.kat !== undefined || change.newValue.text !== undefined));
                  
                  // Check if this is status field
                  const isStatusField = field === 'status';
                  
                  // Check if this is type field
                  const isTypeField = field === 'type';
                  
                  // Check if this is createdBy field
                  const isCreatedByField = field === 'createdBy';
                  
                  // Check if this is customFields
                  const isCustomFieldsData = field === 'customFields';
                  
                  // Mező ikon meghatározása
                  const getFieldIcon = (fieldName: string) => {
                    switch (fieldName) {
                      case 'firstName': case 'lastName': return '👤';
                      case 'companyName': return '🏢';
                      case 'email': return '📧';
                      case 'phone': return '📱';
                      case 'address': return '📍';
                      case 'taxNumber': return '🏛️';
                      case 'registrationNumber': return '📋';
                      case 'bankAccountNumber': return '💳';
                      case 'isActive': case 'status': return '🔄';
                      case 'type': return '🏷️';
                      case 'notes': return '📝';
                      case 'documents': return '📄';
                      case 'organizations': case 'organizationIds': return '🏢';
                      case 'createdBy': return '👨‍💼';
                      case 'contactPersons': return '👥';
                      case 'customFields': return '📊';
                      default: return '📊';
                    }
                  };
                  
                  return (
                    <div key={field} className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow ${isPodRelated || isLargeObject ? 'overflow-x-auto' : ''}`}>
                      <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-base">{getFieldIcon(field)}</span>
                          <span>{t(`customers.history.fieldNames.${field}`) !== `customers.history.fieldNames.${field}` ? t(`customers.history.fieldNames.${field}`) : (t(`customers.fields.${field}`) !== `customers.fields.${field}` ? t(`customers.fields.${field}`) : field)}</span>
                        </div>
                        <div className="flex space-x-1">
                        {isPodRelated && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              ⚡ POD
                            </span>
                        )}
                        {isContactData && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              👥 Kapcsolat
                            </span>
                        )}
                        {isConsumptionData && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                              📊 Fogyasztás
                            </span>
                        )}
                        {isOrganizationsData && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                              🏢 Szervezetek
                            </span>
                        )}
                        {isContractPeriodData && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                              📋 Szerződés
                            </span>
                        )}
                        {isOfferPriceData && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                              💰 Ajánlat
                            </span>
                        )}
                        {isKatData && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200">
                              🔋 KÁT
                            </span>
                        )}
                        {isCustomFieldsData && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              📊 Egyedi mezők
                            </span>
                        )}
                        </div>
                      </div>
                    
                      {entry.changeType === 'update' && (
                        <div className={`${isPodRelated || isLargeObject ? 'space-y-3' : 'grid grid-cols-2 gap-4'}`}>
                          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-2">
                            <div className="text-xs font-medium text-red-700 dark:text-red-400 mb-1 flex items-center">
                              {t('customers.history.oldValue')}
                            </div>
                            {isContactData ? (
                              renderContactBadges(change.oldValue)
                            ) : isConsumptionData ? (
                              renderConsumptionBadges(change.oldValue)
                            ) : isOrganizationsData ? (
                              renderOrganizationsBadges(change.oldValue, organizationsList)
                            ) : isContractPeriodData ? (
                              renderContractPeriodBadges(change.oldValue, t)
                            ) : isOfferPriceData ? (
                              renderOfferPriceBadges(change.oldValue)
                            ) : isKatData ? (
                              renderKatBadges(change.oldValue)
                            ) : isStatusField ? (
                              renderStatusBadge(change.oldValue, t)
                            ) : isTypeField ? (
                              renderTypeBadge(change.oldValue, t)
                            ) : isCreatedByField ? (
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                  <UserIcon className="w-3 h-3 mr-1" />
                                  {userNames[change.oldValue] || change.oldValue}
                                </span>
                              </div>
                            ) : isCustomFieldsData ? (
                              renderCustomFieldsBadges(change.oldValue, customFields)
                            ) : (
                              <div className={`text-xs text-gray-600 dark:text-gray-400 ${isPodRelated || isLargeObject ? 'whitespace-pre-wrap' : 'break-words'}`}>
                                {formatHistoryValue(change.oldValue)}
                              </div>
                            )}
                          </div>
                          
                          {isPodRelated && <div className="border-t my-2 border-gray-200"></div>}
                          
                          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-2">
                            <div className="text-xs font-medium text-green-700 dark:text-green-400 mb-1 flex items-center">
                              {t('customers.history.newValue')}
                            </div>
                            {isContactData ? (
                              renderContactBadges(change.newValue)
                            ) : isConsumptionData ? (
                              renderConsumptionBadges(change.newValue)
                            ) : isOrganizationsData ? (
                              renderOrganizationsBadges(change.newValue, organizationsList)
                            ) : isContractPeriodData ? (
                              renderContractPeriodBadges(change.newValue, t)
                            ) : isOfferPriceData ? (
                              renderOfferPriceBadges(change.newValue)
                            ) : isKatData ? (
                              renderKatBadges(change.newValue)
                            ) : isStatusField ? (
                              renderStatusBadge(change.newValue, t)
                            ) : isTypeField ? (
                              renderTypeBadge(change.newValue, t)
                            ) : isCreatedByField ? (
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                  <UserIcon className="w-3 h-3 mr-1" />
                                  {userNames[change.newValue] || change.newValue}
                                </span>
                              </div>
                            ) : isCustomFieldsData ? (
                              renderCustomFieldsBadges(change.newValue, customFields)
                            ) : (
                              <div className={`text-xs text-gray-800 dark:text-gray-200 ${isPodRelated || isLargeObject ? 'whitespace-pre-wrap' : 'break-words'}`}>
                                {formatHistoryValue(change.newValue)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    
                      {entry.changeType === 'create' && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3">
                          <div className="text-xs font-medium text-green-700 dark:text-green-400 mb-2 flex items-center">
                            {t('customers.history.initialValue')}
                          </div>
                          {isContactData ? (
                            renderContactBadges(change.newValue)
                          ) : isConsumptionData ? (
                            renderConsumptionBadges(change.newValue)
                          ) : isOrganizationsData ? (
                            renderOrganizationsBadges(change.newValue, organizationsList)
                          ) : isContractPeriodData ? (
                            renderContractPeriodBadges(change.newValue, t)
                          ) : isOfferPriceData ? (
                            renderOfferPriceBadges(change.newValue)
                          ) : isKatData ? (
                            renderKatBadges(change.newValue)
                          ) : isStatusField ? (
                            renderStatusBadge(change.newValue, t)
                          ) : isTypeField ? (
                            renderTypeBadge(change.newValue, t)
                          ) : isCreatedByField ? (
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                <UserIcon className="w-3 h-3 mr-1" />
                                {userNames[change.newValue] || change.newValue}
                              </span>
                            </div>
                          ) : isCustomFieldsData ? (
                            renderCustomFieldsBadges(change.newValue, customFields)
                          ) : (
                            <div className={`text-xs text-gray-800 dark:text-gray-200 ${isPodRelated || isLargeObject ? 'whitespace-pre-wrap' : 'break-words'}`}>
                              {formatHistoryValue(change.newValue)}
                            </div>
                          )}
                        </div>
                      )}
                    
                      {entry.changeType === 'delete' && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                          <div className="text-xs font-medium text-red-700 dark:text-red-400 mb-2 flex items-center">
                            {t('customers.history.deletedValue')}
                          </div>
                          {isContactData ? (
                            renderContactBadges(change.oldValue)
                          ) : isConsumptionData ? (
                            renderConsumptionBadges(change.oldValue)
                          ) : isOrganizationsData ? (
                            renderOrganizationsBadges(change.oldValue, organizationsList)
                          ) : isContractPeriodData ? (
                            renderContractPeriodBadges(change.oldValue, t)
                          ) : isOfferPriceData ? (
                            renderOfferPriceBadges(change.oldValue)
                          ) : isKatData ? (
                            renderKatBadges(change.oldValue)
                          ) : isStatusField ? (
                            renderStatusBadge(change.oldValue, t)
                          ) : isTypeField ? (
                            renderTypeBadge(change.oldValue, t)
                          ) : isCreatedByField ? (
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                <UserIcon className="w-3 h-3 mr-1" />
                                {userNames[change.oldValue] || change.oldValue}
                              </span>
                            </div>
                          ) : (
                            <div className={`text-xs text-gray-600 dark:text-gray-400 ${isPodRelated || isLargeObject ? 'whitespace-pre-wrap' : 'break-words'}`}>
                              {formatHistoryValue(change.oldValue)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Fogyasztás mező formázása ezres tagolással
const formatNumber = (value: string): string => {
  if (!value) return '';
  
  // Eltávolítjuk a nem-szám karaktereket, kivéve a tizedespontot
  const cleanValue = value.replace(/[^0-9.]/g, '');
  
  // Szétválasztjuk a tizedes rész előtti és utáni részre
  const parts = cleanValue.split('.');
  
  // Formázzuk az egész részt ezres tagolással
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Összerakjuk az eredményt
  return parts.length > 1 ? parts.join('.') : parts[0];
};

// Helper function to format values for display in history
const formatHistoryValue = (value: any): string | JSX.Element => {
  if (value === null || value === undefined) {
    return '—';
  }
  
  if (typeof value === 'object') {
    // Format objects nicely
    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      
      // Special handling for POD arrays
      if (value[0] && (value[0].id || value[0].address)) {
        return value.map(pod => {
          const id = pod.id || '—';
          const address = pod.address || '—';
          const consumption = pod.annualConsumption ? formatNumber(pod.annualConsumption) : '0';
          return `${id.substring(0, 15)}... (${address.substring(0, 15)}... ${consumption} kWh)`;
        }).join('\n');
      }
      
      // Special handling for organization IDs
      if (typeof value[0] === 'string' && value[0].length > 10) {
        return `[${value.length} szervezet]`;
      }
      
      return `[${value.length} items]`;
    }
    
    // Special handling for addresses
    if (value.street !== undefined || value.city !== undefined || value.postalCode !== undefined || value.country !== undefined) {
      // Ha minden mező üres, akkor "Nincs cím" üzenet
      if (!value.street && !value.city && !value.postalCode && !value.country) {
        return 'Nincs cím megadva';
      }
      return formatAddress(value);
    }
    
    // Special handling for POD objects
    if (value.id && value.address) {
      return `${value.id} (${value.address}, ${value.annualConsumption ? formatNumber(value.annualConsumption) : '0'} kWh)`;
    }
    
    // Special handling for contact person data with badges
    if ((value.name !== undefined || value.email !== undefined || value.phone !== undefined || 
         value.fax !== undefined || value.position !== undefined) && 
        Object.keys(value).some(key => ['name', 'email', 'phone', 'fax', 'position'].includes(key))) {
      
      // Mivel JSX.Element-et nem tudunk visszaadni stringként, ezért formázott szöveget adunk vissza
      // A tényleges badge-eket a renderContactBadges függvénnyel fogjuk megjeleníteni
      const parts = [];
      
      if (value.name) parts.push(`👤 ${value.name}`);
      if (value.position) parts.push(`💼 ${value.position}`);
      if (value.email) parts.push(`📧 ${value.email}`);
      if (value.phone) parts.push(`📱 ${value.phone}`);
      if (value.fax) parts.push(`📠 ${value.fax}`);
      
      return parts.join('\n');
    }
    
    // Special handling for consumption data with badges
    if ((value.consumption !== undefined || value.date !== undefined || value.dueDate !== undefined || 
         value.power !== undefined || value.profileNumber !== undefined) && 
        Object.keys(value).some(key => ['consumption', 'date', 'dueDate', 'power', 'profileNumber'].includes(key))) {
      
      const parts = [];
      
      if (value.consumption) parts.push(`🔋 ${formatNumber(value.consumption)} kWh`);
      if (value.date) parts.push(`📅 ${new Date(value.date).toLocaleDateString('hu-HU')}`);
      if (value.dueDate) parts.push(`🕒 ${value.dueDate} nap`);
      if (value.power) parts.push(`🔌 ${formatNumber(value.power)} kW`);
      if (value.profileNumber) parts.push(`🔢 ${value.profileNumber}`);
      
      return parts.join('\n');
    }
    
    // Special handling for contract period
    if ((value.startDate !== undefined || value.endDate !== undefined || value.price !== undefined) &&
        Object.keys(value).some(key => ['startDate', 'endDate', 'price'].includes(key))) {
      
      const parts = [];
      
      if (value.startDate) parts.push(`📅 ${value.startDate}`);
      if (value.endDate) parts.push(`🗓️ ${value.endDate}`);
      if (value.price) parts.push(`💰 ${formatNumber(value.price)} Ft/kWh`);
      
      return parts.join('\n');
    }
    
    // For other objects, limit to prevent overflow
    const stringified = JSON.stringify(value, null, 2);
    if (stringified.length > 100) {
      return stringified.substring(0, 100) + '...';
    }
    return stringified;
  }
  
  
  if (typeof value === 'boolean') {
    return value ? '✅ Igen' : '❌ Nem';
  }
  
  if (typeof value === 'string') {
    // Ha üres string vagy csak whitespace
    if (!value || value.trim() === '') {
      return '—';
    }
    
    // Speciális értékek kezelése
    if (value === 'organization') {
      return '🏢 Szervezet';
    }
    if (value === 'individual') {
      return '👤 Magánszemély';
    }
    if (value === 'active') {
      return '✅ Aktív';
    }
    if (value === 'inactive') {
      return '❌ Inaktív';
    }
    
    return value;
  }
  
  return String(value);
};

// Helper function to render contact badges
const renderContactBadges = (value: any): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span>—</span>;
  }
  
  return (
    <div className="flex flex-wrap gap-2">
      {value.name && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value.name}
        </span>
      )}
      {value.position && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {value.position}
        </span>
      )}
      {value.email && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {value.email}
        </span>
      )}
      {value.phone && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          {value.phone}
        </span>
      )}
      {value.fax && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
          {value.fax}
        </span>
      )}
    </div>
  );
};

// Helper function to render consumption badges
const renderConsumptionBadges = (value: any): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span>—</span>;
  }
  
  return (
    <div className="flex flex-wrap gap-2">
      {value.consumption && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {value.consumption} kWh
        </span>
      )}
      {value.date && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {new Date(value.date).toLocaleDateString('hu-HU')}
        </span>
      )}
      {value.dueDate && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          {value.dueDate} nap
        </span>
      )}
      {value.power && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          {formatNumber(value.power)} kW
        </span>
      )}
      {value.profileNumber && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {value.profileNumber}
        </span>
      )}
    </div>
  );
};

// Helper function to render organizations badges
const renderOrganizationsBadges = (value: any, orgs: SimpleOrganization[] = []): JSX.Element => {
  if (!value || !Array.isArray(value)) {
    return <span>—</span>;
  }
  
  if (value.length === 0) {
    return <span className="text-gray-500 dark:text-gray-400">Nincs szervezet</span>;
  }
  
  return (
    <div className="flex flex-wrap gap-2">
      {value.map((orgId: string, index: number) => {
        const org = orgs.find((o: SimpleOrganization) => o.id === orgId);
        return org ? (
          <span
            key={org.id}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-600 border border-primary-100"
          >
            <BuildingOffice2Icon className="w-4 h-4 mr-2" />
            {org.name}
          </span>
        ) : (
          <span
            key={index}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
          >
            <BuildingOffice2Icon className="w-4 h-4 mr-2" />
            {orgId.substring(0, 6)}...
          </span>
        );
      })}
    </div>
  );
};

// Helper function to render contract period badges
const renderContractPeriodBadges = (value: any, t: any = (key: string) => key): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span>—</span>;
  }
  
  // Fogyasztás mező formázása ezres tagolással
  const formatPrice = (price: string): string => {
    if (!price) return '';
    
    // Eltávolítjuk a nem-szám karaktereket, kivéve a tizedespontot
    const cleanValue = price.replace(/[^0-9.]/g, '');
    
    // Szétválasztjuk a tizedes rész előtti és utáni részre
    const parts = cleanValue.split('.');
    
    // Formázzuk az egész részt ezres tagolással
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    
    // Összerakjuk az eredményt
    return parts.length > 1 ? parts.join('.') : parts[0];
  };
  
  return (
    <div className="flex flex-wrap gap-2">
      {value.startDate && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value.startDate}
        </span>
      )}
      {value.endDate && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          {value.endDate}
        </span>
      )}
      {value.price && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {formatPrice(value.price)} Ft/kWh
        </span>
      )}
    </div>
  );
};

// Helper function to render offer price badges
const renderOfferPriceBadges = (value: any): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span>—</span>;
  }
  
  return (
    <div className="flex flex-wrap gap-2">
      {value.price && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          {value.price}
        </span>
      )}
      {value.currency && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {value.currency}
        </span>
      )}
    </div>
  );
};

// Helper function to render custom fields badges
const renderCustomFieldsBadges = (value: any, customFields: any[]): JSX.Element => {
  if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
    return <span className="text-xs text-gray-500 dark:text-gray-400">—</span>;
  }
  
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(value).map(([key, val]) => {
        if (val === undefined || val === null || val === '') return null;
        
        // Find field definition to get the name and type
        const fieldDef = customFields.find(f => f.key === key);
        const fieldName = fieldDef?.name || key;
        const fieldType = fieldDef?.type;
        
        return (
          <div key={key} className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200 dark:border-purple-700">
            <span className="font-semibold text-purple-700 dark:text-purple-300">{fieldName}:</span>
            <span className="ml-1 text-gray-800 dark:text-gray-200">
              {Array.isArray(val) ? (
                <span className="inline-flex flex-wrap gap-1 ml-1">
                  {val.map((v: string, idx: number) => (
                    <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-200 dark:bg-purple-800/50 text-purple-900 dark:text-purple-200">
                      {v}
                    </span>
                  ))}
                </span>
              ) : fieldType === 'checkbox' ? (
                <span className="font-medium">{val ? '✓ Igen' : '✗ Nem'}</span>
              ) : fieldType === 'date' ? (
                <span>{typeof val === 'string' || typeof val === 'number' ? new Date(val).toLocaleDateString('hu-HU') : String(val)}</span>
              ) : (
                <span>{String(val)}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Helper function to render KAT badges
const renderKatBadges = (value: any): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span>—</span>;
  }
  
  return (
    <div className="flex flex-wrap gap-2">
      {value.kat && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-fuchsia-100 text-fuchsia-800">
          {value.kat}
        </span>
      )}
      {value.text && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
          {value.text}
        </span>
      )}
    </div>
  );
};

// Helper function to render status badge
const renderStatusBadge = (value: string, t: any = (key: string) => key): JSX.Element => {
  if (!value) {
    return <span>—</span>;
  }
  
  const isActive = value === 'active';
  
  return (
    <div className="flex flex-wrap gap-2">
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        <span className="mr-1">{isActive ? '✅' : '❌'}</span> 
        {isActive ? 'Aktív' : 'Inaktív'}
      </span>
    </div>
  );
};

// Helper function to render type badge
const renderTypeBadge = (value: string, t: any = (key: string) => key): JSX.Element => {
  if (!value) {
    return <span>—</span>;
  }
  
  const isOrganization = value === 'organization';
  
  return (
    <div className="flex flex-wrap gap-2">
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isOrganization ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
        <span className="mr-1">{isOrganization ? '🏢' : '👤'}</span> 
        {isOrganization ? 'Szervezet' : 'Magánszemély'}
      </span>
    </div>
  );
};

// Formázott értékek megjelenítése a táblázatban
const formatDisplayValue = (key: string, value: string | undefined): string => {
  if (!value) return '-';
  
  // Fogyasztás és MÉF értékek formázása ezres tagolással
  if (key.includes('MÉF') || 
      key.includes('Éves mennyiség') || 
      key.includes('Fogyasztás') || 
      key === 'Szerződéses ár' || 
      key === 'Ajánlati ár' ||
      key === 'Teljesítmény (kW)') {
    return formatNumber(value);
  }
  
  // Fizetési határidő formázása "nap" szöveggel
  if (key === 'Fizetési határidő' && !value.includes('nap')) {
    return `${value} nap`;
  }
  
  return value;
};

export default CustomerViewModal;


